// OWNER: LG-ADMIN. Pure logic behind the Lead Search page (LEADGEN-SPEC §10): deep links, geography and areas, the
// New search rules, estimate and run text, stage progress, the funnel, import eligibility, gold-set keys, key masking
// and the suppression list. No DOM, no fetch: tests/lg-admin-*.test.ts import this under the worker tsconfig.
import type { Campaign, Country } from '../../../src/shared/api';
import { looksHealthcare } from '../../../src/shared/api-core';
import {
  GATE_A_ORDER, SEARCH_STAGES, engineOffersFor,
  type CheckVerdict, type DetectorEval, type DropGate, type GeoOption, type HeadToHead, type LeadFlag, type LeadOffer,
  type LeadPlaceStatus, type LeadSearch, type LedgerVendor, type NicheProfile, type NicheProfileRow, type NicheStatus,
  type PilotReport, type ScoreReason, type SearchArea, type SearchCreateRequest, type SearchEstimate, type SearchFunnel,
  type SearchMode, type SearchReport, type SearchStage, type SearchStatus, type SmokeCheck, type SmokeCheckKey,
  type SmokeReport, type StageProgress, type SuppressionEntry, type SuppressionKind, type Tier, type LeadResultsQuery,
} from '../../../src/shared/api-leadgen';
import { normalizeNanp } from '../admin/phone';
import type { Problem } from '../admin/validate';

export type { Problem };
const err = (message: string, where = ''): Problem => ({ level: 'error', where, message });
const warn = (message: string, where = ''): Problem => ({ level: 'warn', where, message });

// =====================================================================================================================
// Tabs and deep links: /lead-search/<tab>[/<searchId>]
// =====================================================================================================================

export type LeadSearchTab = 'niches' | 'new' | 'searches' | 'results' | 'checks' | 'spend';
export const LEAD_SEARCH_TABS: ReadonlyArray<{ key: LeadSearchTab; label: string }> = [
  { key: 'niches', label: 'Niches' },
  { key: 'new', label: 'New search' },
  { key: 'searches', label: 'Searches' },
  { key: 'results', label: 'Results' },
  { key: 'checks', label: 'Checks' },
  { key: 'spend', label: 'Spend' },
];
export const DEFAULT_TAB: LeadSearchTab = 'searches';

/** params.rest from app.tsx ('results/12', 'new', undefined) -> the tab and an optional search id. */
export function parseDeepLink(rest: string | undefined): { tab: LeadSearchTab; searchId: number | null } {
  const [first, second] = (rest ?? '').split('/').filter(Boolean);
  const tab = LEAD_SEARCH_TABS.find((t) => t.key === first)?.key ?? DEFAULT_TAB;
  const n = second !== undefined && /^\d+$/.test(second) ? Number(second) : NaN;
  return { tab, searchId: tab === 'results' && Number.isSafeInteger(n) && n > 0 ? n : null };
}

export function deepLinkPath(tab: LeadSearchTab, searchId?: number | null): string {
  return `/lead-search/${tab}${tab === 'results' && searchId ? `/${searchId}` : ''}`;
}

/** Niches -> "Smoke test" / "Pilot" opens New search pre-filled: /lead-search/new?niche=plumbing_hvac&v=2&mode=smoke */
export function newSearchLink(nicheKey: string, version: number, mode: Exclude<SearchMode, 'single'>): string {
  return `/lead-search/new?${new URLSearchParams({ niche: nicheKey, v: String(version), mode }).toString()}`;
}
export function parseNewSearchPrefill(search: string): { niche: string | null; version: number | null; mode: Exclude<SearchMode, 'single'> | null } {
  const p = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const v = Number(p.get('v'));
  const mode = p.get('mode');
  return {
    niche: p.get('niche')?.trim() || null,
    version: Number.isSafeInteger(v) && v > 0 ? v : null,
    mode: mode === 'smoke' || mode === 'pilot' || mode === 'full' ? mode : null,
  };
}

// =====================================================================================================================
// Formatting
// =====================================================================================================================

/** Dollars with as many decimals as the amount has (2 to 5): $14.00, $0.004, $0.00375. Never rounds a cost up. */
export function usd(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const a = Math.abs(n);
  for (const d of [2, 3, 4, 5]) {
    const f = 10 ** d;
    if (Math.abs(Math.round(a * f) / f - a) < 1e-9) return `${sign}$${a.toFixed(d)}`;
  }
  return `${sign}$${a.toFixed(5)}`;
}
/** Budgets and caps are whole cents. */
export const usd2 = (n: number | null | undefined): string => (n === null || n === undefined || !Number.isFinite(n) ? '—' : `$${n.toFixed(2)}`);
export const int = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : n.toLocaleString('en-US'));
export const share = (x: number | null | undefined, digits = 0): string => (x === null || x === undefined ? 'not measured' : `${(x * 100).toFixed(digits)}%`);

/** Money in a list or table cell: whole cents ($0.11, $14.00), 4 decimals only under a cent ($0.0035), and
 *  "<$0.0001" for a positive amount smaller than that (never "$0.0000" for a real cost). Rounded to the nearest
 *  cent, so the cell always fits; put `usd(n)` (every decimal) in the cell's title so nothing is hidden. */
export function money(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a === 0) return '$0.00';
  const cents = Math.round((a + Number.EPSILON) * 100) / 100;
  if (cents >= 0.01) return `${sign}$${cents.toFixed(2)}`;
  const tenths = Math.round((a + Number.EPSILON) * 10000) / 10000;
  return tenths >= 0.0001 ? `${sign}$${tenths.toFixed(4)}` : `${sign === '-' ? '>-' : '<'}$0.0001`;
}

/** Dollars in the estimate total that no vendor line carries. LG-DATA's estimate.ts rounds units to 0.001 and drops
 *  lines whose units round to 0 (a smoke's Sonnet line: 0.00042 M tokens), while their dollars stay in totalUsd. The
 *  panel shows this as its own row so the lines always add up to the total; 0 when nothing is missing. */
export function unitemisedUsd(e: Pick<SearchEstimate, 'byVendor' | 'totalUsd'>): number {
  const gap = e.totalUsd - e.byVendor.reduce((a, l) => a + l.usd, 0);
  return gap > 1e-7 ? Math.round(gap * 1e7) / 1e7 : 0;
}

/** The estimate's Units column. anthropic.* units are millions of input-token equivalents (prices.ts priceUsd), so
 *  they show as tokens; a count under 0.01 shows "<0.01" rather than rounding a real line to "0". */
export function unitsText(op: string, units: number): { text: string; title: string | undefined } {
  if (!Number.isFinite(units)) return { text: '—', title: undefined };
  if (op.startsWith('anthropic.')) {
    const tokens = Math.round(units * 1_000_000);
    return {
      text: `≈${tokens.toLocaleString('en-US')} tokens`,
      title: `${units} million input-token equivalents (the estimated dollars ÷ the model's input price per million tokens)`,
    };
  }
  if (units === 0) return { text: '0', title: undefined };
  if (Math.abs(units) < 0.01) return { text: '<0.01', title: String(units) };
  return { text: (Math.round(units * 100) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 }), title: String(units) };
}

// =====================================================================================================================
// Labels
// =====================================================================================================================

export const OFFER_LABELS: Record<LeadOffer, string> = {
  front_desk: 'Get Answered (AI receptionist)',
  website: 'Website build',
  gbp: 'Local SEO / Google profile',
};

export const MODE_RULES: Record<Exclude<SearchMode, 'single'>, {
  label: string; limitPerTerm: number | null; budgetUsd: number | null; oneCity: boolean; allTerms: boolean; hint: string;
}> = {
  smoke: { label: 'Smoke', limitPerTerm: 3, budgetUsd: 2, oneCity: true, allTerms: true, hint: 'One city, every term, 3 places per term, $2 budget. Required once per profile version.' },
  // P0-3: the server sizes a pilot's cap to its terms (estimate × 1.3), never under $12; budgetUsd here is that floor.
  pilot: { label: 'Pilot', limitPerTerm: 60, budgetUsd: 12, oneCity: true, allTerms: true, hint: 'One city, every term, 60 places per term; the budget is sized to the terms, $12 or more. Measures precision and the real rates.' },
  full: { label: 'Full', limitPerTerm: null, budgetUsd: null, oneCity: false, allTerms: false, hint: 'Any areas and terms. Needs an active profile.' },
};
/** Full-mode default per-term cap: the maxCrawledPlacesPerSearch in the §4.3 S1 actor input example (on DataForSEO, the
 *  Maps SERP depth: 200 = two billed pages of 100). */
export const FULL_DEFAULT_LIMIT = 200;
/** §3.2 default of leadgen_hand_pick_cap_per_search. No route returns the live value yet (NOTES-lg-admin.md). */
export const HAND_PICK_CAP_DEFAULT = 25;

export const STATUS_LABELS: Record<SearchStatus, string> = {
  draft: 'draft', estimated: 'estimated', running: 'running', paused: 'paused', paused_budget: 'paused: budget',
  review: 'ready for review', imported: 'imported', failed: 'failed', canceled: 'canceled',
};
export type Tone = 'ok' | 'warn' | 'bad' | 'info' | '';
export const STATUS_TONES: Record<SearchStatus, Tone> = {
  draft: '', estimated: '', running: 'info', paused: 'warn', paused_budget: 'warn', review: 'ok', imported: 'ok', failed: 'bad', canceled: '',
};
export const NICHE_TONES: Record<NicheStatus, Tone> = { draft: '', approved: 'info', active: 'ok', retired: '' };
export const TIER_TONES: Record<Tier, Tone> = { A: 'ok', B: 'info', C: 'warn', D: '' };
export const PLACE_STATUS_TONES: Record<LeadPlaceStatus, Tone> = {
  raw: '', dropped: 'bad', held: 'warn', survivor: '', scored: 'info', approved: 'ok', rejected: 'bad', imported: 'ok',
};

/** Plain words for every drop gate (§4.3 S2 Gate A, S3 Gate B). */
export const DROP_GATE_LABELS: Record<DropGate, string> = {
  no_phone: 'No usable phone',
  toll_free: 'Toll-free number',
  closed: 'Permanently closed',
  country: 'Wrong country',
  state_closed: 'State or province closed to calling',
  healthcare_ca: 'Canadian healthcare (CRTC)',
  label_deny: 'Category on the deny list',
  not_in_niche: 'Not in the niche',
  lead_gen_listing: 'Lead-gen listing',
  chain: 'Chain (3+ locations)',
  too_big: 'Too big (review count over the ceiling)',
  dnc_internal: 'On our DNC list',
  suppressed: 'Suppressed (existing relationship)',
  already_ours: 'Already ours (DNC, meeting or cooldown)',
  dupe: 'Duplicate phone in this search',
  no_licence: 'No active licence found',
  servicetitan: 'Uses ServiceTitan',
  business_lines_only: 'Not a landline (business-lines-only state)',
  toll_free_line: 'Toll-free line',
};
/** Funnel order: Gate A as the spec lists it (not_in_niche comes from the in-niche check after label_deny), then Gate B. */
export const DROP_ORDER: readonly DropGate[] = (() => {
  const a = [...GATE_A_ORDER];
  a.splice(a.indexOf('label_deny') + 1, 0, 'not_in_niche');
  return [...a, 'servicetitan', 'business_lines_only', 'toll_free_line'];
})();

export const FLAG_LABELS: Record<LeadFlag, { label: string; tone: Tone; title: string }> = {
  hand_pick_state: { label: 'hand-pick state', tone: 'warn', title: `Hand-pick only: at most ${HAND_PICK_CAP_DEFAULT} per state import, and the queue never auto-selects it.` },
  two_locations: { label: '2 locations', tone: 'info', title: 'Part of a two-location group.' },
  suspicious_reviews: { label: 'suspicious reviews', tone: 'bad', title: 'Google removed reviews; every review-based hook is off.' },
  has_web_vendor: { label: 'has a web vendor', tone: 'warn', title: 'The site names a web vendor.' },
  is_ad: { label: 'paid ad', tone: '', title: 'Found as a Maps ad.' },
  answering_service: { label: 'answering service', tone: 'warn', title: 'The site mentions an answering service.' },
  native_ai_software: { label: 'AI phone software', tone: 'warn', title: 'Uses software with a built-in AI phone agent.' },
  commercial_only: { label: 'commercial only', tone: 'warn', title: 'The site says commercial customers only.' },
  provisional_detector: { label: 'provisional detector', tone: 'warn', title: "A hook's detector hasn't passed its gold set (0.95 on 30), so it can't open a call." },
  held_temp_closed: { label: 'temporarily closed', tone: 'warn', title: 'Held; re-checked in 30 days.' },
  region_inferred: { label: 'service area', tone: 'info', title: 'Google hides this address; the state comes from the search area, the area code and the map pin.' },
};

export const VENDOR_LABELS: Record<LedgerVendor, string> = {
  apify: 'Apify', dataforseo: 'DataForSEO', google_places: 'Google Places', google_psi: 'PageSpeed', twilio: 'Twilio Lookup',
  anthropic: 'Anthropic (lead gen)', millionverifier: 'MillionVerifier', apollo: 'Apollo',
};
export const vendorLabel = (v: string): string => (VENDOR_LABELS as Record<string, string>)[v] ?? v;

// =====================================================================================================================
// API error details (409 search_blocked -> {blockers}, 409 activation_blocked -> {gates})
// =====================================================================================================================

interface FailureLike { status?: unknown; body?: { error?: unknown; message?: unknown; details?: unknown } }
function detailsOf(e: unknown): Record<string, unknown> | null {
  const b = (e && typeof e === 'object' && 'body' in e) ? (e as FailureLike).body : null;
  const d = b && typeof b === 'object' ? b.details : null;
  return d && typeof d === 'object' && !Array.isArray(d) ? d as Record<string, unknown> : null;
}
/** SearchBlocked.blockers from a failed run, else []. */
export function blockersFromError(e: unknown): string[] {
  const d = detailsOf(e);
  return Array.isArray(d?.blockers) ? (d!.blockers as unknown[]).filter((x): x is string => typeof x === 'string' && !!x.trim()) : [];
}
/** ActivationBlocked.gates from a failed activate, else []. */
export function gatesFromError(e: unknown): Array<{ key: string; ok: boolean; detail: string }> {
  const d = detailsOf(e);
  if (!Array.isArray(d?.gates)) return [];
  return (d!.gates as unknown[]).flatMap((g) => {
    if (!g || typeof g !== 'object') return [];
    const o = g as Record<string, unknown>;
    return typeof o.key === 'string' ? [{ key: o.key, ok: o.ok === true, detail: typeof o.detail === 'string' ? o.detail : '' }] : [];
  });
}
export const ACTIVATION_GATE_LABELS: Record<string, string> = {
  in_niche_precision: 'In-niche precision ≥ 0.85 on n ≥ 30',
  healthcare: 'Healthcare gate',
  state: 'State gates',
  smoke: 'Smoke test passed',
};

// =====================================================================================================================
// Geography (§10 tab 2; GET /api/leadgen/geo reads state_rules live)
// =====================================================================================================================

/** 'CA-ON' -> 'ON'. */
export const stateAbbr = (code: string): string => code.includes('-') ? code.slice(code.indexOf('-') + 1) : code;
/** Written the way the §4.3 example writes it: "Hamilton, ON, Canada". */
export const COUNTRY_NAME: Record<Country, string> = { US: 'USA', CA: 'Canada' };
/** Apify countryCode enum values (actor build 0.14.759 input schema: 'us' = United States, 'ca' = Canada). */
export const APIFY_COUNTRY_CODE: Record<Country, string> = { US: 'us', CA: 'ca' };
export const regionWord = (c: Country): string => (c === 'CA' ? 'province' : 'state');

export interface GeoChoice {
  option: GeoOption;
  selectable: boolean;
  handPick: boolean;
  reason: string | null;   // why it can't be picked (closed, or no licence data for a licence niche)
}

/** Three groups, each alphabetical. Closed states are never selectable. A licence niche (dispensaries) can only search
 *  a jurisdiction with a licence adapter: no licence data, no dispensary search (§6.3). */
export function geoChoices(options: GeoOption[], profile: NicheProfile | null): { open: GeoChoice[]; handPick: GeoChoice[]; closed: GeoChoice[] } {
  const licenced = new Set((profile?.licence_sources ?? []).filter((s) => s.adapter !== 'none').map((s) => s.jurisdiction.toUpperCase()));
  const choice = (o: GeoOption): GeoChoice => {
    if (o.group === 'closed') return { option: o, selectable: false, handPick: false, reason: o.reason?.trim() || 'Closed to calling.' };
    if (profile?.licence_required && !licenced.has(o.code.toUpperCase())) {
      return { option: o, selectable: false, handPick: o.group === 'hand_pick', reason: `No licence data for ${o.name} yet, so no ${profile.display_name} search here.` };
    }
    return { option: o, selectable: true, handPick: o.group === 'hand_pick', reason: null };
  };
  const by = (g: GeoOption['group']) => options.filter((o) => o.group === g).sort((a, b) => a.name.localeCompare(b.name)).map(choice);
  return { open: by('open'), handPick: by('hand_pick'), closed: by('closed') };
}

// =====================================================================================================================
// Areas: per picked state, whole state/province or typed cities
// =====================================================================================================================

export interface AreaPick { code: string; name: string; whole: boolean; citiesText: string }

const COUNTRY_WORDS: Record<Country, string[]> = {
  US: ['usa', 'us', 'u.s.', 'u.s.a.', 'united states', 'united states of america'],
  CA: ['canada', 'can'],
};

/** One city per line. "Hamilton", "Hamilton, ON" and "Hamilton, Ontario, Canada" all mean Hamilton when Ontario is the
 *  state; "Kingston, NY" under Ontario is an error, not a guess. Duplicates are merged. */
export function parseCityLines(text: string, st: { code: string; name: string }, country: Country): { cities: string[]; problems: string[] } {
  const cities: string[] = [];
  const problems: string[] = [];
  const seen = new Set<string>();
  const okParts = new Set([stateAbbr(st.code).toLowerCase(), st.name.toLowerCase(), ...COUNTRY_WORDS[country]]);
  text.split(/\r?\n/).forEach((line, i) => {
    const clean = line.replace(/\s+/g, ' ').trim();
    if (!clean) return;
    const parts = clean.split(',').map((p) => p.trim()).filter(Boolean);
    const city = parts[0] ?? '';
    const extra = parts.slice(1).filter((p) => !okParts.has(p.toLowerCase()));
    if (extra.length) {
      problems.push(`${st.name}, line ${i + 1}: “${clean}” names somewhere other than ${st.name}. Put it under its own ${regionWord(country)}, or type the city name only.`);
      return;
    }
    if (!/\p{L}/u.test(city)) { problems.push(`${st.name}, line ${i + 1}: “${clean}” isn't a city name.`); return; }
    if (city.length > 80) { problems.push(`${st.name}, line ${i + 1}: that city name is too long.`); return; }
    const k = city.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    cities.push(city);
  });
  return { cities, problems };
}

/** SearchArea[] for the create request.
 *  - city:   {label "Hamilton, ON", locationQuery "Hamilton, ON, Canada" (§4.3 example), state "Ontario"}
 *  - region: {label "Ontario", countryCode 'ca', state "Ontario"}. Apify's `state` input takes the full name
 *    ("e.g. Massachusetts", build 0.14.759), so `state` is the state_rules name in both kinds; lib/phone.ts
 *    normalizeRegion maps it back to the code for gating. */
export function buildAreas(picks: AreaPick[], country: Country): { areas: SearchArea[]; problems: string[] } {
  const areas: SearchArea[] = [];
  const problems: string[] = [];
  for (const p of picks) {
    const abbr = stateAbbr(p.code);
    if (p.whole) {
      areas.push({ kind: 'region', label: p.name, countryCode: APIFY_COUNTRY_CODE[country], state: p.name });
      continue;
    }
    const parsed = parseCityLines(p.citiesText, p, country);
    problems.push(...parsed.problems);
    if (parsed.cities.length === 0 && parsed.problems.length === 0) {
      problems.push(`Add at least one city for ${p.name}, or pick “Whole ${regionWord(country)}”.`);
    }
    for (const c of parsed.cities) {
      areas.push({ kind: 'city', label: `${c}, ${abbr}`, locationQuery: `${c}, ${abbr}, ${COUNTRY_NAME[country]}`, state: p.name });
    }
  }
  return { areas, problems };
}

/** "Hamilton, ON" · "Hamilton, ON and Toronto, ON" · "A, B and 3 more". */
export function areasSummary(areas: Array<Pick<SearchArea, 'label'>>): string {
  const l = areas.map((a) => a.label);
  if (l.length === 0) return 'no areas';
  if (l.length === 1) return l[0];
  if (l.length === 2) return `${l[0]} and ${l[1]}`;
  if (l.length === 3) return `${l[0]}, ${l[1]} and ${l[2]}`;
  return `${l[0]}, ${l[1]} and ${l.length - 2} more`;
}

// =====================================================================================================================
// New search: form rules, request, signature, confirmation text
// =====================================================================================================================

export interface NewSearchForm {
  niche: NicheProfileRow | null;
  mode: Exclude<SearchMode, 'single'>;
  offer: LeadOffer;
  country: Country;
  picks: AreaPick[];
  terms: string[];
  limitPerTerm: number;
  handPickConfirmed: boolean;
}

/** Why this niche version can't run this mode (§4.3 S0 step 4, §6.2), in plain words; [] = it can. */
export function nicheModeProblems(n: NicheProfileRow, mode: Exclude<SearchMode, 'single'>): string[] {
  const name = `${n.displayName} v${n.version}`;
  if (n.status === 'draft') return [`${name} is a draft. Approve it in Niches first.`];
  if (n.status === 'retired') return [`${name} is retired. Pick a newer version.`];
  if (mode === 'full' && n.status !== 'active') return [`Full searches need an active profile; ${name} is ${n.status}. Pass the smoke and pilot, then activate it.`];
  if (mode === 'pilot' && n.smokePassed !== true) {
    return [n.smokePassed === false ? `${name}'s smoke test failed. Fix it and pass a smoke before a pilot.` : `Run a smoke test on ${name} before a pilot.`];
  }
  return [];
}

/** CRTC: Canadian healthcare facilities can't be cold called (SPEC §5.4). The server refuses too. */
export function canadaHealthcareBlocked(n: NicheProfileRow | null, country: Country): boolean {
  if (!n || country !== 'CA') return false;
  return n.profile.healthcare_ca_risk?.flag === true || looksHealthcare(n.key, n.displayName, ...(n.profile.search_terms ?? []));
}
export const CRTC_SENTENCE = "Canadian healthcare facilities can't be cold called (CRTC). Pick a US search or a different niche.";

/** Everything wrong with the form before it goes to the server. The server re-checks all of it. */
export function newSearchProblems(form: NewSearchForm, geo: GeoOption[] | null, listHealthBreaches: string[] = []): Problem[] {
  const out: Problem[] = [];
  const n = form.niche;
  if (!n) return [err('Pick a niche.', 'niche')];
  for (const m of nicheModeProblems(n, form.mode)) out.push(err(m, 'niche'));
  if (canadaHealthcareBlocked(n, form.country)) out.push(err(CRTC_SENTENCE, 'country'));
  if (form.mode === 'full') for (const b of listHealthBreaches) out.push(err(`List health: ${b} This blocks the next Full search in this niche.`, 'niche'));

  if (form.picks.length === 0) out.push(err(`Pick at least one ${regionWord(form.country)}.`, 'geo'));
  if (geo) {
    const choices = geoChoices(geo, n.profile);
    const all = [...choices.open, ...choices.handPick, ...choices.closed];
    for (const p of form.picks) {
      const c = all.find((x) => x.option.code === p.code);
      if (!c) out.push(err(`${p.name} isn't in the ${form.country} list any more. Untick it.`, 'geo'));
      else if (!c.selectable) out.push(err(`${p.name}: ${c.reason}`, 'geo'));
    }
    const handPicked = form.picks.filter((p) => all.find((x) => x.option.code === p.code)?.handPick);
    if (handPicked.length && !form.handPickConfirmed) {
      out.push(err(`Tick “Leads here are hand-pick only; at most ${HAND_PICK_CAP_DEFAULT} per state import” to include ${handPicked.map((p) => p.name).join(', ')}.`, 'geo'));
    }
  }
  const { areas, problems } = buildAreas(form.picks, form.country);
  for (const p of problems) out.push(err(p, 'areas'));
  const rules = MODE_RULES[form.mode];
  if (rules.oneCity && form.picks.length > 0 && problems.length === 0 && (areas.length !== 1 || areas[0].kind !== 'city')) {
    out.push(err(`A ${rules.label.toLowerCase()} runs on exactly one city (you have ${areas.length === 1 ? 'a whole region' : `${areas.length} areas`}).`, 'areas'));
  }

  const profileTerms = new Set(n.profile.search_terms ?? []);
  if (form.terms.length === 0) out.push(err('Turn on at least one search term.', 'terms'));
  for (const t of form.terms) if (!profileTerms.has(t)) out.push(err(`“${t}” isn't one of this profile's terms.`, 'terms'));
  if (rules.allTerms && form.terms.length !== profileTerms.size) out.push(err(`A ${rules.label.toLowerCase()} uses every term.`, 'terms'));

  const limit = rules.limitPerTerm ?? form.limitPerTerm;
  if (!Number.isInteger(limit) || limit < 1) out.push(err('Places per term must be a whole number of at least 1.', 'limit'));
  if (form.mode === 'full' && form.terms.length && areas.length) {
    const cap = form.terms.length * limit * areas.length;
    if (cap > 20000) out.push(warn(`Up to ${int(cap)} scrape events (${form.terms.length} terms × ${int(limit)} × ${areas.length} areas). Check the estimate before you run it.`, 'limit'));
  }
  return out;
}

/** The create request, or null while the form has errors. Smoke and pilot force their per-term limit and all terms. */
export function toCreateRequest(form: NewSearchForm, geo: GeoOption[] | null, listHealthBreaches: string[] = []): SearchCreateRequest | null {
  if (!form.niche || newSearchProblems(form, geo, listHealthBreaches).some((p) => p.level === 'error')) return null;
  const rules = MODE_RULES[form.mode];
  const profileTerms = form.niche.profile.search_terms;
  return {
    nicheKey: form.niche.key,
    nicheVersion: form.niche.version,
    mode: form.mode,
    offer: form.offer,
    country: form.country,
    areas: buildAreas(form.picks, form.country).areas,
    terms: rules.allTerms ? [...profileTerms] : profileTerms.filter((t) => form.terms.includes(t)),   // profile order
    limitPerTerm: rules.limitPerTerm ?? form.limitPerTerm,
  };
}

/** Stable identity of a request: an estimate belongs to one signature; any change makes it stale. */
export function requestSignature(req: SearchCreateRequest | null): string {
  if (!req) return '';
  return JSON.stringify([req.nicheKey, req.nicheVersion, req.mode, req.offer, req.country, req.areas.map((a) => [a.kind, a.label, a.state]), req.terms, req.limitPerTerm]);
}

/** "Spend up to $14.00 on Plumbing, heating and HVAC in Hamilton, ON (Full)?" */
export function runConfirmText(a: { budgetUsd: number; nicheName: string; areas: Array<Pick<SearchArea, 'label'>>; mode: Exclude<SearchMode, 'single'> }): string {
  return `Spend up to ${usd2(a.budgetUsd)} on ${a.nicheName} in ${areasSummary(a.areas)} (${MODE_RULES[a.mode].label})?`;
}

/** Budget checks against the estimate (§5.3): the server refuses a run whose ESTIMATE passes the month cap. */
export function budgetProblems(budgetUsd: number, est: SearchEstimate | null, mode: Exclude<SearchMode, 'single'>, fixedUsd?: number | null): Problem[] {
  const out: Problem[] = [];
  if (!Number.isFinite(budgetUsd) || budgetUsd <= 0) return [err('The budget must be more than $0.', 'budget')];
  if (Math.abs(Math.round(budgetUsd * 100) - budgetUsd * 100) > 1e-6) out.push(err('The budget is in whole cents.', 'budget'));
  const fixed = fixedUsd === undefined ? MODE_RULES[mode].budgetUsd : fixedUsd;
  if (fixed !== null && Math.abs(budgetUsd - fixed) > 1e-9) out.push(err(`A ${MODE_RULES[mode].label.toLowerCase()} has a fixed ${usd2(fixed)} budget.`, 'budget'));
  if (!est) return out;
  if (budgetUsd < est.totalUsd) out.push(warn(`The budget is under the estimate (${usd(est.totalUsd)}), so the search will pause when it reaches ${usd2(budgetUsd)}.`, 'budget'));
  if (est.monthSpentUsd + est.totalUsd > est.monthCapUsd) {
    out.push(err(`The estimate takes this month to ${usd(est.monthSpentUsd + est.totalUsd)}, past the ${usd2(est.monthCapUsd)} cap.`, 'budget'));
  } else if (est.monthSpentUsd + budgetUsd > est.monthCapUsd) {
    out.push(warn(`Spending the whole budget would take this month to ${usd(est.monthSpentUsd + budgetUsd)}, past the ${usd2(est.monthCapUsd)} cap; the month cap stops it first.`, 'budget'));
  }
  return out;
}

/** The niche versions a search can start from, newest first per key; retired ones are left out. */
export function nicheChoices(rows: NicheProfileRow[]): NicheProfileRow[] {
  return rows.filter((r) => r.status !== 'retired')
    .sort((a, b) => a.displayName.localeCompare(b.displayName) || a.key.localeCompare(b.key) || b.version - a.version);
}
/** Default pick: the newest active version, else the newest approved one, else nothing. */
export function defaultNiche(rows: NicheProfileRow[], key?: string | null, version?: number | null): NicheProfileRow | null {
  if (key && version) { const exact = rows.find((r) => r.key === key && r.version === version); if (exact) return exact; }
  const pool = key ? rows.filter((r) => r.key === key) : rows;
  const newest = (st: NicheStatus) => pool.filter((r) => r.status === st).sort((a, b) => b.version - a.version)[0] ?? null;
  return newest('active') ?? newest('approved') ?? null;
}
/** Offer to sort by: the profile's first ranked offer, else Get Answered (front_desk). */
export const defaultOffer = (p: NicheProfile | null): LeadOffer => p?.offers_ranked?.[0]?.offer ?? 'front_desk';

// =====================================================================================================================
// Searches tab
// =====================================================================================================================

export type SearchAction = 'pause' | 'resume' | 'cancel' | 'raise';
/** What PJ can do with a search in each status. Resume after paused_budget works once the budget is raised. */
export function searchActions(status: SearchStatus): SearchAction[] {
  switch (status) {
    case 'running': return ['pause', 'raise', 'cancel'];
    case 'paused': return ['resume', 'raise', 'cancel'];
    case 'paused_budget': return ['raise', 'resume', 'cancel'];
    case 'draft': case 'estimated': return ['cancel'];
    default: return [];
  }
}
export const isActiveStatus = (s: SearchStatus): boolean => s === 'running' || s === 'paused' || s === 'paused_budget';

/** §10 tab 3's five bars. S7 (people) and S8 (hooks, report) finish the scoring step. */
export const PROGRESS_GROUPS: ReadonlyArray<{ label: string; stages: readonly SearchStage[] }> = [
  { label: 'Discovered', stages: ['S1'] },
  { label: 'Gated', stages: ['S2'] },
  { label: 'Enriched', stages: ['S3', 'S4'] },
  { label: 'Classified', stages: ['S5'] },
  { label: 'Scored', stages: ['S6', 'S7', 'S8'] },
];
export interface ProgressBar { label: string; finished: number; total: number; failed: number; state: 'pending' | 'active' | 'done' }

export function progressBars(stages: StageProgress[], search: Pick<LeadSearch, 'stage' | 'status'>): ProgressBar[] {
  const at = search.stage ? SEARCH_STAGES.indexOf(search.stage) : -1;
  const allDone = search.status === 'review' || search.status === 'imported';
  return PROGRESS_GROUPS.map((g) => {
    const rows = stages.filter((s) => g.stages.includes(s.stage));
    const total = rows.reduce((a, s) => a + s.queued + s.running + s.waiting + s.done + s.failed + s.canceled, 0);
    const finished = rows.reduce((a, s) => a + s.done + s.failed + s.canceled, 0);
    const failed = rows.reduce((a, s) => a + s.failed, 0);
    const first = SEARCH_STAGES.indexOf(g.stages[0]);
    const last = SEARCH_STAGES.indexOf(g.stages[g.stages.length - 1]);
    const state: ProgressBar['state'] = allDone || (at > last) ? 'done' : at >= first ? 'active' : 'pending';
    return { label: g.label, finished, total, failed, state };
  });
}

export const failedTaskTotal = (f: SearchFunnel | null | undefined): number =>
  f ? Object.values(f.failedTasks ?? {}).reduce((a, n) => a + (n || 0), 0) : 0;

/** "612 raw · 401 survivors · A 40 · B 88 · C 120 · D 153" for rows that aren't running. */
export function funnelLine(f: SearchFunnel | null | undefined): string {
  if (!f) return 'no results yet';
  const t = f.tiers ?? { A: 0, B: 0, C: 0, D: 0 };
  return `${int(f.raw)} raw · ${int(f.survivors)} survivors · A ${int(t.A)} · B ${int(t.B)} · C ${int(t.C)} · D ${int(t.D)}`;
}

/** A raise must be above the current budget; returns the problem in words, or null. */
export function raiseProblem(current: number, next: number, spent: number): string | null {
  if (!Number.isFinite(next) || next <= 0) return 'Enter the new budget in dollars.';
  if (Math.abs(Math.round(next * 100) - next * 100) > 1e-6) return 'The budget is in whole cents.';
  if (next <= current) return `The new budget must be more than the current ${usd2(current)}.`;
  if (next <= spent) return `The new budget must be more than what's already spent or reserved (${usd(spent)}).`;
  return null;
}

// =====================================================================================================================
// Results tab
// =====================================================================================================================

/** Drop counts in funnel order; gates the UI doesn't know yet come last under their code. Zero counts are left out. */
export function funnelDrops(f: SearchFunnel): Array<{ gate: string; label: string; count: number }> {
  const d = f.dropped ?? {};
  const known = DROP_ORDER.filter((g) => (d[g] ?? 0) > 0).map((g) => ({ gate: g as string, label: DROP_GATE_LABELS[g], count: d[g] }));
  const unknown = Object.keys(d).filter((g) => !(DROP_ORDER as readonly string[]).includes(g) && d[g] > 0).sort()
    .map((g) => ({ gate: g, label: g.replace(/_/g, ' '), count: d[g] }));
  return [...known, ...unknown];
}
export const droppedTotal = (f: SearchFunnel): number => Object.values(f.dropped ?? {}).reduce((a, n) => a + (n || 0), 0);

/** Reason text as the server wrote it ("+35 two phone complaints (Aug, May 2026)"); points are prefixed if missing. */
export function reasonText(r: ScoreReason): string {
  const t = r.text.trim();
  if (/^[+\-−]\s?\d/.test(t)) return t;
  return `${r.points >= 0 ? '+' : ''}${r.points} ${t}`;
}

export function rowReviewActions(status: LeadPlaceStatus): { approve: boolean; reject: boolean } {
  return { approve: status === 'scored' || status === 'rejected', reject: status === 'scored' || status === 'approved' };
}

export interface CampaignChoice { campaign: Campaign; ok: boolean; reason: string | null; nicheMatches: boolean }
/** §4.3 S9 campaign checks, shown before the click (the server enforces them): country matches, draft or live, the offer
 *  family is scored, and no Canadian healthcare niche. Matching niche first, then live, then name. */
export function importableCampaigns(campaigns: Campaign[], search: Pick<LeadSearch, 'country' | 'nicheKey'>, profile: NicheProfile | null): CampaignChoice[] {
  return campaigns
    .filter((c) => (c.status === 'draft' || c.status === 'live') && c.country === search.country)
    .map((c) => {
      const nicheMatches = c.niche.trim().toLowerCase() === search.nicheKey.toLowerCase();
      if (!engineOffersFor(c.offer)) return { campaign: c, ok: false, reason: `Its offer “${c.offer}” isn't scored by the engine.`, nicheMatches };
      if (search.country === 'CA' && (profile?.healthcare_ca_risk?.flag || looksHealthcare(c.niche, search.nicheKey))) {
        return { campaign: c, ok: false, reason: CRTC_SENTENCE, nicheMatches };
      }
      return { campaign: c, ok: true, reason: null, nicheMatches };
    })
    .sort((a, b) => Number(b.ok) - Number(a.ok) || Number(b.nicheMatches) - Number(a.nicheMatches)
      || Number(b.campaign.status === 'live') - Number(a.campaign.status === 'live') || a.campaign.name.localeCompare(b.campaign.name));
}

/** State filter options for the results table: the search's own areas, as the 2-letter code lead_places.state_prov
 *  stores ('ON'), named from the live /geo list. An area state /geo doesn't know is offered as written. */
export function searchStateOptions(areas: SearchArea[], geo: GeoOption[] | null): Array<{ value: string; label: string }> {
  const out = new Map<string, string>();
  for (const a of areas) {
    const s = a.state.trim();
    const hit = (geo ?? []).find((g) => g.name.toLowerCase() === s.toLowerCase() || g.code.toUpperCase() === s.toUpperCase()
      || stateAbbr(g.code).toUpperCase() === s.toUpperCase());
    if (hit) out.set(stateAbbr(hit.code), hit.name);
    else if (s) out.set(s, s);
  }
  return [...out.entries()].map(([value, label]) => ({ value, label })).sort((x, y) => x.label.localeCompare(y.label));
}

// =====================================================================================================================
// Smoke and pilot reports (§5.4, §6.2): LeadSearch.report, shown on the Results tab and summarised on Niches
// =====================================================================================================================

/** §5.4's eleven checks in the spec's order, with a short name for summaries ("failed: charge cap"). A Record, so the
 *  compiler fails if the contract's SmokeCheckKey gains or loses a check. The first four are discovery slots: the
 *  report's label names the vendor's own check (Apify webhook and run cap, or DataForSEO live answers and reservations). */
const SMOKE_SHORT: Record<SmokeCheckKey, string> = {
  webhook: 'discovery answered',
  apify_fields: 'place fields',
  opening_hours: 'opening hours',
  charge_cap: 'charge cap',
  dfs_reviews: 'DataForSEO reviews',
  rank_check_url: 'rank check_url',
  psi: 'PageSpeed',
  twilio: 'Twilio line type',
  anthropic_batch: 'Anthropic call',
  ledger_accuracy: 'ledger accuracy',
  places_no_writes: 'Places no writes',
};
export const SMOKE_CHECKS: ReadonlyArray<{ key: SmokeCheckKey; short: string }> =
  (Object.keys(SMOKE_SHORT) as SmokeCheckKey[]).map((key) => ({ key, short: SMOKE_SHORT[key] }));
export const smokeCheckShort = (key: string): string => SMOKE_CHECKS.find((c) => c.key === key)?.short ?? key;

export const isSmokeReport = (r: SearchReport | null | undefined): r is SmokeReport => !!r && r.kind === 'smoke';
export const isPilotReport = (r: SearchReport | null | undefined): r is PilotReport => !!r && r.kind === 'pilot';

export interface SmokeSummary {
  passed: boolean;             // the server's verdict AND every one of the 11 checks present and passing
  passedCount: number;
  total: number;               // always 11: a check missing from the report counts as not passed
  failed: SmokeCheck[];
  missing: SmokeCheckKey[];    // §5.4 checks the report doesn't carry (fail loudly, never assume a pass)
  text: string;                // "10 of 11 checks passed; failed: charge cap"
}
export function smokeSummary(r: SmokeReport): SmokeSummary {
  const checks = r.checks ?? [];
  const failed = checks.filter((c) => !c.pass);
  const missing = SMOKE_CHECKS.map((c) => c.key).filter((k) => !checks.some((c) => c.key === k));
  const passedCount = SMOKE_CHECKS.filter((s) => checks.some((c) => c.key === s.key && c.pass)).length;
  const total = SMOKE_CHECKS.length;
  const bits = [
    failed.length ? `failed: ${failed.map((c) => smokeCheckShort(c.key)).join(', ')}` : '',
    missing.length ? `missing from the report: ${missing.map(smokeCheckShort).join(', ')}` : '',
  ].filter(Boolean);
  return {
    passed: r.passed && failed.length === 0 && missing.length === 0,
    passedCount, total, failed, missing,
    text: `${passedCount} of ${total} checks passed${bits.length ? `; ${bits.join('; ')}` : ''}`,
  };
}

/** Niches rows whose latest smoke failed: the searches whose reports the table loads to say which checks failed. */
export function failedSmokeIds(rows: Array<Pick<NicheProfileRow, 'smokePassed' | 'smokeSearchId'>>): number[] {
  return [...new Set(rows.filter((r) => r.smokePassed === false && r.smokeSearchId !== null).map((r) => r.smokeSearchId as number))].sort((a, b) => a - b);
}

/** §5.2 priors the pilot's measured values replace for a niche; tests check they equal LG-DATA's estimate.ts PRIORS. */
export const ESTIMATE_PRIORS = { k: 1.25, s: 0.7, regexHitRate: 0.3, abRate: 0.4, aboutPageRate: 0.6 } as const;

/** When a report was written, in PJ's time zone. */
export const reportTime = (iso: string | null | undefined): string => (iso
  ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/Toronto', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })
  : '—');

/** "12 of 40 (30%)"; "not measured" when nothing was measured. */
export function hitRateText(hits: number, measured: number): string {
  if (!measured) return 'not measured';
  return `${int(hits)} of ${int(measured)} (${Math.round((hits / measured) * 100)}%)`;
}

/** A 0..1 rate from the pilot, or "not measured". */
export const rateText = (x: number | null | undefined): string => (x === null || x === undefined ? 'not measured' : x.toFixed(2));

/** §6.2 in-niche precision against the §6.2 activation bar (≥ 0.85 on n ≥ 30). */
export function pilotPrecisionText(p: PilotReport['inNiche']): { text: string; tone: Tone } {
  const n = p.correct + p.wrong;
  if (p.precision === null) return { text: `not measured (${int(p.checked)} checked)`, tone: '' };
  const meets = p.precision >= SCORING_MIN_PRECISION && n >= MIN_CHECKED;
  const why = meets ? 'meets the activation bar' : n < MIN_CHECKED ? `check ${MIN_CHECKED - n} more to decide` : `under ${SCORING_MIN_PRECISION}`;
  return { text: `${p.precision.toFixed(2)} on n = ${n}: ${why}`, tone: meets ? 'ok' : n < MIN_CHECKED ? '' : 'bad' };
}

/** §4.6: DataForSEO can replace Apify discovery when it finds ≥ 90% of Apify's in-niche places and its phone and
 *  domain fill are within 5 points. Anything unmeasured means "can't tell", never a pass. */
export function headToHeadVerdict(h: HeadToHead | null): { text: string; tone: Tone } {
  if (!h) return { text: 'not measured', tone: '' };
  const vals = [h.dfsCoverageOfApifyInNiche, h.apifyPhoneFill, h.dfsPhoneFill, h.apifyDomainFill, h.dfsDomainFill];
  if (vals.some((v) => v === null)) return { text: "Can't tell yet: part of the comparison wasn't measured.", tone: '' };
  const [cov, ap, dp, ad, dd] = vals as number[];
  const within = (a: number, b: number) => Math.abs(a - b) <= 0.05 + 1e-9;
  const ok = cov >= 0.9 - 1e-9 && within(ap, dp) && within(ad, dd);
  return ok
    ? { text: 'DataForSEO could replace Apify discovery here (≥ 90% coverage, fill within 5 points).', tone: 'ok' }
    : { text: 'Keep Apify: DataForSEO misses the 90% coverage or 5-point fill rule.', tone: '' };
}

// =====================================================================================================================
// Checks tab (§7.3 gold sets)
// =====================================================================================================================

/** §7.3 bars; tests/lg-admin-logic.test.ts checks they equal LG-SIGNALS' signals/eval.ts constants. */
export const OPENER_MIN_PRECISION = 0.95;
export const SCORING_MIN_PRECISION = 0.85;
export const MIN_CHECKED = 30;

/** Keys 1 / 2 / 3 = Correct / Wrong / Unsure, never while typing a note, never with a modifier. */
export function verdictForKey(key: string, opts: { typing: boolean; modifier: boolean }): CheckVerdict | null {
  if (opts.typing || opts.modifier) return null;
  return key === '1' ? 'correct' : key === '2' ? 'wrong' : key === '3' ? 'unsure' : null;
}
export function isTypingTarget(tag: string | null | undefined, contentEditable = false): boolean {
  const t = (tag ?? '').toUpperCase();
  return contentEditable || t === 'INPUT' || t === 'TEXTAREA' || t === 'SELECT';
}

export interface EvalStanding { opener: 'eligible' | 'below' | 'need_more'; scoring: 'on' | 'off'; needMore: number; text: string; tone: Tone }
/** The server's openerEligible / scoringOn are authoritative; this puts them in words. */
export function evalStanding(ev: DetectorEval): EvalStanding {
  const needMore = Math.max(0, MIN_CHECKED - ev.n);
  const p = ev.precision === null ? null : ev.precision.toFixed(2);
  if (ev.openerEligible) return { opener: 'eligible', scoring: 'on', needMore: 0, text: `${p} on n = ${ev.n}: can open calls`, tone: 'ok' };
  if (!ev.scoringOn) return { opener: 'below', scoring: 'off', needMore, text: `${p ?? '—'} on n = ${ev.n}: switched off for scoring (under ${SCORING_MIN_PRECISION})`, tone: 'bad' };
  if (needMore > 0) return { opener: 'need_more', scoring: 'on', needMore, text: `${p ?? 'no verdicts'} on n = ${ev.n}: check ${needMore} more to decide`, tone: '' };
  return { opener: 'below', scoring: 'on', needMore: 0, text: `${p} on n = ${ev.n}: mid-call only (under ${OPENER_MIN_PRECISION})`, tone: 'warn' };
}

// =====================================================================================================================
// Spend tab: keys, months, suppression list
// =====================================================================================================================

/** Never more than the last 4 characters of a key, whatever the server sends. */
export function maskLast4(last4: string | null | undefined): string {
  const s = (last4 ?? '').trim();
  return s ? `••••${s.slice(-4)}` : '—';
}

/** 'YYYY-MM' for this month and the n-1 before it (the Spend month picker). `today` is 'YYYY-MM-DD' in OPS_TZ. */
export function monthOptions(today: string, n = 12): string[] {
  let y = Number(today.slice(0, 4));
  let m = Number(today.slice(5, 7));
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m -= 1;
    if (m === 0) { m = 12; y -= 1; }
  }
  return out;
}
export function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export const SUPPRESSION_DEFAULT_REASON = 'existing relationship';
export const SUPPRESSION_KIND_LABELS: Record<SuppressionKind, string> = { phone: 'Phone', domain: 'Domain', place_id: 'Google place ID' };
const PLACE_ID_RE = /^[A-Za-z0-9_-]{10,300}$/;
const DOMAIN_RE = /^(?=.{3,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

/** One suppression value in the form the server matches on: phone -> E.164, domain -> bare lowercase host without www,
 *  place_id -> as Google gives it. */
export function normalizeSuppressionValue(kind: SuppressionKind, raw: string): { value: string } | { error: string } {
  const s = raw.trim();
  if (!s) return { error: 'Empty value.' };
  if (kind === 'phone') {
    const p = normalizeNanp(s);
    return p ? { value: p.e164 } : { error: `“${s}” isn't a valid US/Canadian phone number.` };
  }
  if (kind === 'domain') {
    let h = s.toLowerCase().replace(/^[a-z][a-z0-9+.-]*:\/\//, '').replace(/^[^@/]*@/, '');
    h = h.split(/[/?#]/)[0].replace(/:\d+$/, '').replace(/\.$/, '').replace(/^www\d?\./, '');
    return DOMAIN_RE.test(h) ? { value: h } : { error: `“${s}” isn't a domain like example.com.` };
  }
  return PLACE_ID_RE.test(s) ? { value: s } : { error: `“${s}” doesn't look like a Google place ID (letters, digits, _ and -).` };
}

/** Kind from the value itself: a valid NANP number is a phone; anything with a dot is a domain; else a place ID. */
export function guessSuppressionKind(raw: string): SuppressionKind {
  const s = raw.trim();
  if (normalizeNanp(s)) return 'phone';
  if (/\./.test(s) || /^[a-z][a-z0-9+.-]*:\/\//i.test(s)) return 'domain';
  return 'place_id';
}

export interface SuppressionDraft { kind: SuppressionKind; value: string; reason: string; line: number }
/** Bulk paste: one per line, "value" or "value, reason" (comma or tab). Existing and repeated entries are skipped. */
export function parseSuppressionLines(text: string, existing: Array<Pick<SuppressionEntry, 'kind' | 'value'>>, defaultReason = SUPPRESSION_DEFAULT_REASON): {
  rows: SuppressionDraft[]; problems: string[]; skipped: number;
} {
  const have = new Set(existing.map((e) => `${e.kind}|${e.value.toLowerCase()}`));
  const rows: SuppressionDraft[] = [];
  const problems: string[] = [];
  let skipped = 0;
  text.split(/\r?\n/).forEach((line, i) => {
    const t = line.trim();
    if (!t) return;
    const cut = t.search(/[\t,]/);
    const rawValue = (cut >= 0 ? t.slice(0, cut) : t).trim();
    const reason = (cut >= 0 ? t.slice(cut + 1) : '').trim() || defaultReason;
    const kind = guessSuppressionKind(rawValue);
    const n = normalizeSuppressionValue(kind, rawValue);
    if ('error' in n) { problems.push(`Line ${i + 1}: ${n.error}`); return; }
    if (reason.length > 200) { problems.push(`Line ${i + 1}: the reason is over 200 characters.`); return; }
    const k = `${kind}|${n.value.toLowerCase()}`;
    if (have.has(k)) { skipped += 1; return; }
    have.add(k);
    rows.push({ kind, value: n.value, reason, line: i + 1 });
  });
  return { rows, problems, skipped };
}

// =====================================================================================================================
// IMPROVEMENTS-2026-10-01 P0-3, P0-5, P0-6, P0-7, P0-8 and the owner fast path (pure; the tabs only render and call)
// =====================================================================================================================

/** P0-3: the budget a smoke or pilot is fixed at. A pilot's is sized to its terms by the server and stored on the draft
 *  search it creates (budgetUsd), never under MODE_RULES.pilot.budgetUsd. null = Full (the user sets it). */
export function fixedBudgetFor(mode: Exclude<SearchMode, 'single'>, draft: Pick<LeadSearch, 'budgetUsd'> | null): number | null {
  const floor = MODE_RULES[mode].budgetUsd;
  if (floor === null) return null;
  if (mode === 'pilot' && draft && Number.isFinite(draft.budgetUsd) && draft.budgetUsd > floor) return draft.budgetUsd;
  return floor;
}

/** P0-7: where a niche version stands, in one plain line, and whether it can run `mode` now. */
export interface NicheReadiness { group: 'ready' | 'needs' | 'draft' | 'retired'; line: string; runnable: boolean }
export function nicheReadiness(row: NicheProfileRow, mode: Exclude<SearchMode, 'single'>): NicheReadiness {
  const runnable = nicheModeProblems(row, mode).length === 0;
  if (row.status === 'retired') return { group: 'retired', line: 'Retired: pick a newer version.', runnable: false };
  if (row.status === 'draft') return { group: 'draft', line: 'Draft: an admin approves it first.', runnable };
  if (row.status === 'active') {
    const waived = row.activationOverride?.waived ?? [];
    return { group: 'ready', line: waived.length ? 'Ready (activated by the owner before its smoke, pilot and checks).' : 'Ready.', runnable };
  }
  const line = row.smokePassed === null ? 'Approved; smoke test not run yet.'
    : row.smokePassed === false ? 'Smoke failed; fix the profile and run the smoke again.'
      : !row.pilotSearchId ? 'Smoke passed; pilot not run yet.'
        : 'Pilot ran; check 30 places for in-niche precision, then activate.';
  return { group: 'needs', line, runnable };
}

export interface NicheOption { row: NicheProfileRow; label: string; disabled: boolean; readiness: NicheReadiness }
export interface NicheOptionGroup { label: string; options: NicheOption[] }
/** P0-7: the niche picker's groups. Versions show only when a key has more than one non-retired version. Setters don't
 *  see drafts, and see anything not runnable as disabled; admins can pick anything (the form says why it can't run). */
export function nicheOptionGroups(rows: NicheProfileRow[], mode: Exclude<SearchMode, 'single'>, isAdmin: boolean): NicheOptionGroup[] {
  const live = nicheChoices(rows);
  const versions = new Map<string, number>();
  for (const r of live) versions.set(r.key, (versions.get(r.key) ?? 0) + 1);
  const opt = (r: NicheProfileRow): NicheOption => {
    const readiness = nicheReadiness(r, mode);
    return { row: r, label: (versions.get(r.key) ?? 0) > 1 ? `${r.displayName} v${r.version}` : r.displayName, disabled: !isAdmin && !readiness.runnable, readiness };
  };
  const groups: NicheOptionGroup[] = [
    { label: 'Ready for Full searches', options: live.filter((r) => r.status === 'active').map(opt) },
    { label: 'Needs smoke / pilot', options: live.filter((r) => r.status === 'approved').map(opt) },
  ];
  if (isAdmin) groups.push({ label: 'Drafts', options: live.filter((r) => r.status === 'draft').map(opt) });
  return groups.filter((g) => g.options.length > 0);
}
/** The banner a setter sees when no niche can run a Full search. */
export const NO_ACTIVE_NICHE = 'No niche is ready for Full searches yet. Ask PJ to run its pilot (or activate it from Niches).';

/** P0-5: newest version per key that is approved or active and has no passed smoke: what "Smoke selected" offers. */
export function smokeCandidates(rows: NicheProfileRow[]): NicheProfileRow[] {
  const newest = new Map<string, NicheProfileRow>();
  for (const r of rows) if (r.status !== 'retired' && (!newest.has(r.key) || newest.get(r.key)!.version < r.version)) newest.set(r.key, r);
  return [...newest.values()].filter((r) => (r.status === 'approved' || r.status === 'active') && r.smokePassed !== true)
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

const CA_PROVINCE_CODES = new Set(['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT']);
/** "Atlanta, GA" -> { city, state abbr, country } (country from the state code), or a plain problem. */
export function parseSmokeCity(line: string): { city: string; abbr: string; country: Country } | { problem: string } {
  const m = /^\s*([^,]+?)\s*,\s*([A-Za-z]{2})\s*$/.exec(line ?? '');
  if (!m) return { problem: 'Type the smoke city as "City, ST", e.g. "Atlanta, GA" or "Hamilton, ON".' };
  const abbr = m[2].toUpperCase();
  return { city: m[1], abbr, country: CA_PROVINCE_CODES.has(abbr) ? 'CA' : 'US' };
}

export interface SmokeBatchItem { row: NicheProfileRow; request: SearchCreateRequest | null; problem: string | null }
/** P0-5: one smoke create request per selected niche, built exactly like the single-niche New search form (same
 *  toCreateRequest), or the reason that niche can't smoke in that city (closed state, hand-pick, licence, CRTC). */
export function smokeBatchPlan(selected: NicheProfileRow[], cityLine: string, geo: GeoOption[] | null): { country: Country | null; items: SmokeBatchItem[]; problem: string | null } {
  const all = (problem: string, country: Country | null) => ({ country, items: selected.map((row) => ({ row, request: null, problem })), problem });
  const c = parseSmokeCity(cityLine);
  if ('problem' in c) return all(c.problem, null);
  if (!geo) return all('Geography is still loading.', c.country);
  const opt = geo.find((o) => o.code === `${c.country}-${c.abbr}`);
  if (!opt) return all(`${c.abbr} isn't a ${c.country === 'CA' ? 'Canadian province' : 'US state'} the engine knows.`, c.country);
  const items = selected.map((row): SmokeBatchItem => {
    const form: NewSearchForm = {
      niche: row, mode: 'smoke', offer: defaultOffer(row.profile), country: c.country,
      picks: [{ code: opt.code, name: opt.name, whole: false, citiesText: c.city }], terms: [...row.profile.search_terms],
      limitPerTerm: MODE_RULES.smoke.limitPerTerm ?? 3, handPickConfirmed: false,
    };
    const errors = newSearchProblems(form, geo).filter((x) => x.level === 'error');
    return errors.length ? { row, request: null, problem: errors.map((e) => e.message).join(' ') } : { row, request: toCreateRequest(form, geo), problem: null };
  });
  return { country: c.country, items, problem: null };
}
/** A run/estimate blocker that means "stop the whole batch" (the month cap is shared by every smoke). */
export const isMonthCapBlocker = (text: string): boolean => /month(ly)? cap|this month/i.test(text);

/** P0-6: merge freshly loaded check items into the list, one per place, keeping order; capped at `max`. */
export function mergeCheckItems<T extends { placeId: string }>(have: T[], incoming: T[], max = 30): T[] {
  const seen = new Set(have.map((x) => x.placeId));
  const out = [...have];
  for (const x of incoming) { if (out.length >= max) break; if (!seen.has(x.placeId)) { seen.add(x.placeId); out.push(x); } }
  return out;
}
/** P0-6: Activate shows inline only when every activation gate the page can see is met: 30+ in-niche verdicts at 0.85+,
 *  the newest approved version, a passed smoke and a pilot. The server re-checks every gate. */
export function inNicheActivateReady(ev: Pick<DetectorEval, 'n' | 'precision'> | null, row: NicheProfileRow | null): boolean {
  return !!ev && !!row && ev.n >= MIN_CHECKED && (ev.precision ?? 0) >= SCORING_MIN_PRECISION
    && row.status === 'approved' && row.smokePassed === true && row.pilotSearchId !== null;
}
/** P0-6: the newest approved version of a key (what Activate would activate), else null. */
export function newestApproved(rows: NicheProfileRow[], key: string): NicheProfileRow | null {
  return rows.filter((r) => r.key === key && r.status === 'approved').sort((a, b) => b.version - a.version)[0] ?? null;
}

/** P0-8: the filters a CSV export carries (the same ones the table shows; never page or page size). */
export function csvQuery(q: LeadResultsQuery): Omit<LeadResultsQuery, 'page' | 'pageSize'> {
  return { tier: q.tier, status: q.status, state: q.state, flag: q.flag, drop: q.drop, hasHook: q.hasHook };
}

/** Owner fast path: the confirm sentence, naming what is skipped and what never is. */
export function fastActivateText(row: Pick<NicheProfileRow, 'displayName' | 'version'>): string {
  return `Activate ${row.displayName} v${row.version} now? This skips its smoke test, pilot and the 30 in-niche checks, so setters can run Full searches on it today (each still needs your approval to spend). Closed states, Quebec, Canadian healthcare, our DNC list and the suppression list still apply to every search.`;
}
